import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Stack, TextField, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useQueryClient } from "react-query";
import axios from "axios";
import useApiEp from "../hooks/useApiEp";
import { API_ENDPOINT } from "../../constants/constants";
import { getHeader } from "../../utils/util";
import ToolsLayout, { ToolsState } from "./ToolsLayout";
import { invalidateTools } from "./toolsData";

export default function ToolsSettingsPage() {
  const query = useApiEp("user/product-settings"),
    client = useQueryClient();
  const [transport, setTransport] = useState(""),
    [gst, setGst] = useState(""),
    [saving, setSaving] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (query.data && !dirty) {
      setTransport(String(query.data.transportChargePerKg ?? ""));
      setGst(String(query.data.gstPercent ?? ""));
    }
  }, [query.data, dirty]);
  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    if (
      transport === "" ||
      gst === "" ||
      !Number.isFinite(Number(transport)) ||
      !Number.isFinite(Number(gst)) ||
      Number(transport) < 0 ||
      Number(gst) < 0 ||
      Number(gst) > 100
    ) {
      setError("Enter a non-negative transport charge and a GST percentage between 0 and 100.");
      return;
    }
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const response = await axios.post(
        `${API_ENDPOINT}user/add-product-settings`,
        { transportChargePerKg: Number(transport), gstPercent: Number(gst) },
        getHeader(),
      );
      if (response.data?.success === false)
        throw new Error(response.data.message || "Unable to save settings.");
      await invalidateTools(client);
      setDirty(false);
      setNotice("Market settings saved.");
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Unable to save settings.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <ToolsLayout
      title="Market settings"
      description="Maintain the transport and tax settings used by the Tools Market."
    >
      <section className="tools-panel" style={{ maxWidth: 700, margin: "auto" }}>
        <ToolsState loading={query.isLoading} error={query.isError} retry={query.refetch}>
          <Box component="form" onSubmit={submit} sx={{ p: 3 }}>
            <Typography component="h2" variant="h6" sx={{ mb: 3 }}>
              Charges & tax
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {notice && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {notice}
              </Alert>
            )}
            <Stack spacing={3}>
              <TextField
                fullWidth
                required
                label="Transport charge (₹ per kg)"
                type="number"
                value={transport}
                onChange={(event) => {
                  setTransport(event.target.value);
                  setDirty(true);
                }}
                inputProps={{ min: 0, step: 0.01 }}
                disabled={saving}
                helperText="Enter 0 when no transport charge applies."
              />
              <TextField
                fullWidth
                required
                label="GST (%)"
                type="number"
                value={gst}
                onChange={(event) => {
                  setGst(event.target.value);
                  setDirty(true);
                }}
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                disabled={saving}
                helperText="Enter the applicable percentage, from 0 to 100."
              />
              <div className="tools-actions" style={{ justifyContent: "flex-end" }}>
                <Button
                  disabled={saving || !dirty}
                  onClick={() => {
                    setDirty(false);
                    setError("");
                  }}
                >
                  Discard changes
                </Button>
                <LoadingButton type="submit" variant="contained" loading={saving} disabled={!dirty}>
                  Save settings
                </LoadingButton>
              </div>
            </Stack>
          </Box>
        </ToolsState>
      </section>
    </ToolsLayout>
  );
}
